import Error from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";

interface ExpandableInfoSectionProps {
  children: React.ReactNode;
  disabled?: boolean;
}
const ExpandableInfoContainer: React.FC<ExpandableInfoSectionProps> = ({
  children,
  disabled = false,
}) => {
  const theme = useTheme();
  return (
    <Accordion
      disabled={disabled}
      sx={{
        boxShadow: "none",
        backgroundColor: theme.palette.grey50.main,
        borderRadius: 1,
        "&:before": {
          display: "none",
        },
        // Override the disabled styling to keep the background color
        "&.Mui-disabled": {
          backgroundColor: theme.palette.grey50.main,
          color: "inherit",
        },
      }}
    >
      {children ?? <></>}
    </Accordion>
  );
};

const ExpandableInfoContainerSummary: React.FC<ExpandableInfoSectionProps> = ({
  children,
  disabled = false,
}) => {
  const theme = useTheme();
  return (
    <AccordionSummary
      expandIcon={disabled ? null : <ExpandMoreIcon />}
      sx={{
        minHeight: 50,
        px: 2,
        pt: 0.5,
        pb: 1,
        overflow: "hidden",
        "&.Mui-expanded": {
          minHeight: 50,
        },
        "& .MuiAccordionSummary-content": {
          margin: "0",
          overflow: "hidden",
          "&.Mui-expanded": {
            margin: "0",
          },
        },
        "&:hover": {
          backgroundColor: disabled ? "transparent" : theme.palette.grey50.dark,
        },
        cursor: disabled ? "default" : "pointer",
        // Override disabled styling to keep appearance
        "&.Mui-disabled": {
          backgroundColor: "transparent",
          color: "inherit",
          opacity: 1,
        },
      }}
    >
      {children ?? <></>}
    </AccordionSummary>
  );
};

interface ExpandableInfoContainerCombinedProps extends ExpandableInfoSectionProps {
  label: string;
  text: string;
  showWarningChip?: boolean;
  infoTooltip?: string;
}
export const ExpandableInfoContainerCombined: React.FC<ExpandableInfoContainerCombinedProps> = ({
  children,
  label,
  text,
  disabled = false,
  showWarningChip = false,
  infoTooltip,
}) => {
  const theme = useTheme();
  return (
    <ExpandableInfoContainer disabled={disabled}>
      <ExpandableInfoContainerSummary disabled={disabled}>
        <Stack>
          <Stack direction="row" alignItems="center" gap={0.25}>
            <Typography fontSize={11} color="textSecondary">
              {label}
            </Typography>
            <InfoIconTooltip
              tooltip={infoTooltip}
              iconButtonSx={{ color: theme.palette.grey[400] }}
            />
          </Stack>

          <Stack direction="row" alignItems="center" gap={0.5}>
            {showWarningChip && <Error color="secondary" fontSize="small" />}
            <Typography fontSize="0.875rem" variant="body2">
              {text}
            </Typography>
          </Stack>
        </Stack>
      </ExpandableInfoContainerSummary>
      <AccordionDetails sx={{ pb: 1, pt: 0, mt: 2 }}>{children}</AccordionDetails>
    </ExpandableInfoContainer>
  );
};
